import {ReactNode, SyntheticEvent} from 'react';
import {
  Box,
  Collapse,
  FormControlLabel,
  FormControlLabelProps,
  IconButton,
  IconButtonProps,
  Stack,
  SxProps,
  styled,
} from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import {CustomCheckbox} from '../../checkbox/checkbox';

interface ExpandMoreProps extends IconButtonProps {
  expanded: boolean;
}

const ExpandMoreStyled = styled((props: ExpandMoreProps) => {
  const {expanded, ...other} = props;
  return <IconButton {...other} />;
})(({theme, expanded}) => ({
  transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const collapsibleHeaderSx: SxProps = {
  flexDirection: 'row',
  alignItems: 'center',
  userSelect: 'none',
  paddingY: '2px',
};

const formControlSx: SxProps = {
  maxWidth: '100%',
  '& .MuiFormControlLabel-label': {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.2px',
    color: 'var(--dcm-gray)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

// Handle the change event internally for state management before propagating it to the parent component
interface CollapsibleCheckboxProps extends Omit<FormControlLabelProps, 'control' | 'onChange'> {
  children: ReactNode;
  expanded: boolean;
  sx?: SxProps;
  hideHeaderCheck?: boolean;
  toggleExpanded: (expanded: boolean) => void;
  onChange?: (checked: boolean) => void;
}

const CollapsibleCheckbox = ({
  label,
  checked,
  children,
  expanded,
  sx,
  hideHeaderCheck = false,
  toggleExpanded,
  onChange,
}: CollapsibleCheckboxProps) => {
  const handleExpandClick = () => toggleExpanded(!expanded);

  const handleCheckboxChange = (event: SyntheticEvent<Element, Event>, checked: boolean) => {
    event.stopPropagation();

    // Force the nested checkboxes to expand so the user can see that they were all selected/deselected
    toggleExpanded(true);

    if (onChange) onChange(checked);
  };

  return (
    <Box sx={sx}>
      <Stack sx={collapsibleHeaderSx}>
        <Box flexGrow={1} sx={{minWidth: 0}}>
          <FormControlLabel
            control={
              <CustomCheckbox
                sx={{
                  display: hideHeaderCheck ? 'none' : 'inline-flex',
                }}
                onChange={handleCheckboxChange}
              />
            }
            sx={{...formControlSx, marginLeft: hideHeaderCheck ? '10px' : undefined}}
            label={label}
            checked={checked}
          />
        </Box>
        <Box flexShrink={0}>
          <ExpandMoreStyled
            expanded={expanded}
            aria-expanded={expanded}
            onClick={handleExpandClick}
          >
            <ExpandMore htmlColor="var(--dcm-gray)" />
          </ExpandMoreStyled>
        </Box>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack direction="column" gap={0.5} sx={{marginLeft: '20px'}}>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default CollapsibleCheckbox;
